/**
 *
 * VideoEmbedly
 *
 */

import React, { createRef } from 'react';
import PropTypes from 'prop-types';
import playerjs from 'player.js';
import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { connect } from 'react-redux';
import Mixpanel from 'utils/mixpanelService';
import _get from 'lodash/get';
import { withStyles } from '@material-ui/core/styles';
import { incrementNPSPlayCount } from 'containers/Main/actions';
import axios from 'axios';
import _isEmpty from 'lodash/isEmpty';
import Config from 'utils/getEnvConfig';
import LoadingIndicator from 'components/LoadingIndicator';
import ClassNames from 'classnames';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Box, Modal } from '@material-ui/core';
import YouTubeIcon from '@material-ui/icons/YouTube';
import { isIOS } from 'utils/stringUtils';
import { isEmbedded } from '../../utils/embedded';

const {
  EMBEDLY: { API_URL, API_KEY },
} = Config;

const styles = theme => ({
  wrapper: {
    position: 'relative',
  },
  root: {
    '& > iframe': {
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      padding: '10px 0',
    },
  },
  rootSection: {
    backgroundColor: '#000',
    '& > iframe': {
      position: 'inherit',
      padding: 0,
    },
  },
  smallCard: {
    height: '100%',
    '& > iframe': {
      padding: 0,
    },
  },
  thumbnail: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 158,
  },
  bigThumbnail: {
    height: 348,
    '& > img': {
      height: 'auto',
      maxHeight: 348,
    },
  },
  smallThumbnail: {
    height: 'auto',
    maxHeight: 123,
    '& > img': {
      height: 'auto',
      maxHeight: 123,
    },
  },
  image: {
    maxWidth: '100%',
  },
  bigVideo: {
    height: '100%',
    width: '100%',
  },
  videoOverlay: {
    [theme.breakpoints.down('sm')]: {
      display: props => (props.isPage ? 'none' : 'block'),
    },
    top: 0,
    position: 'absolute',
    width: '100%',
    height: '100%',
    cursor: 'pointer',
    background: 'rgba(0,0,0,0.6)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    '&:hover': {
      '& svg > path': {
        fill: '#f00',
      },
    },
  },
  videoOverlayContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    height: '100%',
    color: '#fff',
    '& > svg': {
      transition:
        'fill .1s cubic-bezier(0.4,0,1,1),fill-opacity .1s cubic-bezier(0.4,0,1,1)',
      fill: '#212121',
      fillOpacity: 0.8,
      fontSize: '5.1rem',
    },
  },
  modal: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
  loaderWrapper: {
    position: 'absolute',
    zIndex: -1,
  },
  iframeWrapper: {
    height: 'calc(9/16*100vmin)',
    width: '100%',
    [theme.breakpoints.up('md')]: {
      width: '1024px',
      height: '576px',
    },
    '& > iframe': {
      height: '100%',
      width: '100%',
    },
  },
});

class VideoEmbedly extends React.PureComponent {
  signal = axios.CancelToken.source();

  constructor(props) {
    super(props);
    this.incrementedNPSPlayCount = false;
    this.state = {
      url: '',
      html: '',
      thumbnail_url: '',
      isExpanded: false,
      isIframeLoaded: false,
    };
    this.videoContainerRef = createRef();
    this.seconds = 0;
    this.duration = null;
  }

  async componentDidMount() {
    const { url } = this.props;
    this.fetchImageMetadataAndSetState(url);
  }

  async componentDidUpdate(prevProps, prevState) {
    if (prevProps.url !== this.props.url) {
      this.fetchImageMetadataAndSetState(this.props.url);
    }
    const { html } = this.state;
    if (
      !prevProps.expandedVideoDisplay &&
      !_isEmpty(html) &&
      prevState.html !== html
    ) {
      this.player = this.initPlayer();
      this.player.on('ready', this.initPlayerEvents);
    }
  }

  componentWillUnmount() {
    this.signal.cancel();
    if (this.player !== undefined) {
      this.player.off();
      this.player = undefined;
    }
  }

  fetchImageMetadataAndSetState = async url => {
    try {
      const params = {
        url,
        key: API_KEY,
      };
      const response = await axios.get(
        `${API_URL}?url=${params.url}&key=${params.key}&dnt=1&a=0&height=123`,
        {
          cancelToken: this.signal.token,
        },
      );

      if (
        response.status === 200 &&
        (!_isEmpty(_get(response, 'data.html')) ||
          !_isEmpty(_get(response, 'data.url')))
      ) {
        const {
          html,
          url: dataUrl,
          thumbnail_url = '',
          thumbnail_width,
          thumbnail_height,
        } = response.data;
        const thumbnail =
          thumbnail_width > 300 && thumbnail_height > 120
            ? thumbnail_url
            : this.props.imageUrl;
        this.setState({ html, url: dataUrl, thumbnail_url: thumbnail });
      }
    } catch (error) {
      if (axios.isCancel(error)) {
        // ignore since request was cancelled
      } else {
        this.setState({
          thumbnail_url: this.props.imageUrl,
          html: '',
          url: '',
        });
      }
    }
  };

  trackEvent = (event, data) => {
    const { location, title, id, trackingPath } = this.props;
    const pageName = location.pathname.split('/')[1];
    const finalPageName = pageName ? `${pageName}Page` : 'homePage';
    Mixpanel.track(event, {
      name: title,
      type: 'video',
      page: finalPageName,
      entryId: id,
      ...(trackingPath && { path: trackingPath }),
      ...(data || {}),
    });
  };

  getEmbedlyArgs = () => {
    if (this.props.locale === 'en-US') {
      return '';
    }

    const youtubeVars = [
      `hl=${this.props.locale}`,
      `cc_lang_pref=${this.props.locale}`,
      'cc_load_policy=1',
    ];
    return `&args=${youtubeVars.join('|')}`;
  };

  initPlayer = () => {
    const iframe = this.videoContainerRef.current.querySelector('iframe');
    iframe.src += this.getEmbedlyArgs();
    return new playerjs.Player(iframe);
  };

  getProgressPercentage = () =>
    `${Math.round((this.seconds / (this.duration || 1)) * 100)}%`;

  initPlayerEvents = () => {
    const eventPrefix = this.props.isPage ? 'Resource Page' : 'Resource Card';

    this.player.on('play', () => {
      this.trackEvent(
        this.seconds > 0
          ? `${eventPrefix} - Play - Resume`
          : `${eventPrefix} - Play`,
      );
    });

    this.player.on('timeupdate', data => {
      const { seconds, duration } = data;
      this.seconds = seconds;
      this.duration = duration;
      if (seconds > 60 && !this.incrementedNPSPlayCount) {
        this.props.incrementNPSPlayCount();
        this.incrementedNPSPlayCount = true;
      }
      if (this.props.onProgressUpdate) {
        this.props.onProgressUpdate(seconds, (seconds / duration) * 100);
      }
    });

    this.player.on('pause', () => {
      const eventData = {
        'resource progress': this.getProgressPercentage(),
      };

      this.trackEvent(`${eventPrefix} - Pause`, eventData);
      if (this.props.isPage) {
        this.props.toggleShowFloatingHelpful(true, true);
      }
    });

    this.player.on('ended', () => {
      const eventData = {
        'resource progress': '100%',
      };

      this.trackEvent(`${eventPrefix} - Pause`, eventData);
    });
  };

  expandVideo = e => {
    e.preventDefault();
    e.stopPropagation();
    const history = _get(this, 'props.history');
    const location = _get(history, 'location');
    const isListCarousel =
      _get(location, 'pathname', '').includes('/lists/') &&
      _get(location, 'search', '').includes('view=carousel');

    if (isEmbedded() && !isListCarousel) {
      const route = _get(this, 'props.route');
      const slug = _get(this, 'props.slug');

      if (route && slug && history) {
        history.push(`${route}/${slug}`);
      }
    }

    this.setState({ isExpanded: true });
  };

  onRefSet = ref => {
    if (ref) {
      this.videoContainerRef.current = ref;
      const iframe = this.videoContainerRef.current.querySelector('iframe');
      this.player = this.initPlayer();
      iframe.onload = () => {
        this.setState({ isIframeLoaded: true });
        this.player.on('ready', () => {
          this.initPlayerEvents();
          this.player.setCurrentTime(this.props.initialTime || 0);
          this.player.play();
        });
      };
    }
  };

  handleClose = e => {
    e.preventDefault();
    e.stopPropagation();
    this.setState({ isExpanded: false });
  };

  render() {
    const {
      classes,
      isBig,
      isCard,
      title,
      isSmallCard,
      expandedVideoDisplay,
      className,
    } = this.props;
    const { url, thumbnail_url, html, isIframeLoaded, isExpanded } = this.state;
    if (_isEmpty(url) && _isEmpty(thumbnail_url))
      return <LoadingIndicator card={isCard} />;

    return (
      <div
        href={url}
        className={ClassNames(className, {
          [classes.wrapper]: true,
          [classes.thumbnail]: _isEmpty(html),
          [classes.bigThumbnail]: _isEmpty(html) && isBig,
          [classes.smallThumbnail]: _isEmpty(html) && !isBig,
          [classes.smallCard]: isSmallCard,
        })}
      >
        {!_isEmpty(html) ? (
          <>
            <div
              className={ClassNames(
                classes.root,
                isBig ? classes.bigVideo : undefined,
                !isCard ? classes.rootSection : undefined,
                isSmallCard ? classes.smallCard : undefined,
              )}
              ref={
                this.props.expandedVideoDisplay
                  ? undefined
                  : this.videoContainerRef
              }
              dangerouslySetInnerHTML={{ __html: html }}
            />
            {expandedVideoDisplay && (
              <Box className={classes.videoOverlay} onClick={this.expandVideo}>
                <Box className={classes.videoOverlayContent}>
                  <YouTubeIcon />
                </Box>
              </Box>
            )}
          </>
        ) : (
          <img src={thumbnail_url} className={classes.image} alt={title} />
        )}
        <Modal
          open={isExpanded}
          onClick={this.handleClose}
          className={classes.modal}
        >
          <>
            <div
              ref={this.onRefSet}
              className={classes.iframeWrapper}
              dangerouslySetInnerHTML={{ __html: html }}
            />
            {!isIframeLoaded && (
              <div className={classes.loaderWrapper}>
                <CircularProgress />
              </div>
            )}
          </>
        </Modal>
      </div>
    );
  }
}

VideoEmbedly.propTypes = {
  classes: PropTypes.object,
  imageUrl: PropTypes.any,
  isCard: PropTypes.bool,
  isBig: PropTypes.bool,
  title: PropTypes.string,
  url: PropTypes.string,
  location: PropTypes.object,
  isSmallCard: PropTypes.bool,
  toggleShowFloatingHelpful: PropTypes.func,
  incrementNPSPlayCount: PropTypes.func,
  isPage: PropTypes.bool,
  initialTime: PropTypes.number,
  expandedVideoDisplay: PropTypes.bool,
};

VideoEmbedly.defaultProps = {
  isCard: true,
  isSmallCard: false,
  isPage: false,
};

function mapDispatchToProps(dispatch) {
  return {
    incrementNPSPlayCount: () => dispatch(incrementNPSPlayCount()),
  };
}

const mapStateToProps = state => {
  const { language, siteConfig } = state.main;
  const featuresSiteConfig = siteConfig.find(i => i.title === 'Features');
  const expandedVideoDisplay =
    isIOS() || isEmbedded()
      ? false
      : _get(featuresSiteConfig, 'config.expandedVideoDisplay', false);

  return {
    expandedVideoDisplay,
    locale: language,
  };
};

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withRouter,
  withConnect,
  withStyles(styles),
)(VideoEmbedly);
